import { MessageCircle, Pencil } from "lucide-react";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TxnAmount, TxnTypeBadge } from "@/components/TxnBadges";

const HEAD = "label-overline text-slate-500";

const MessageRow = ({ children }) => (
  <TableRow>
    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
      {children}
    </TableCell>
  </TableRow>
);

const TxnRow = ({ t, onEditDate, onShare }) => (
  <TableRow data-testid={`txn-row-${t.id}`}>
    <TableCell className="font-mono-num text-sm text-slate-600">
      <div className="flex items-center gap-1 whitespace-nowrap">
        {formatDate(t.created_at)}
        {onShare && (
          <Button
            variant="ghost"
            size="icon"
            data-testid={`whatsapp-txn-btn-${t.id}`}
            onClick={() => onShare(t)}
            title="Send on WhatsApp"
            aria-label="Send on WhatsApp"
            className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
          >
            <MessageCircle className="h-3.5 w-3.5" />
          </Button>
        )}
        {onEditDate && (
          <Button
            variant="ghost"
            size="icon"
            data-testid={`edit-txn-date-btn-${t.id}`}
            onClick={() => onEditDate(t)}
            title="Edit date"
            aria-label="Edit date"
            className="h-7 w-7"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </TableCell>
    <TableCell>
      <TxnTypeBadge type={t.type} />
    </TableCell>
    <TableCell className="text-sm text-slate-700">
      {t.note || <span className="text-slate-400">—</span>}
    </TableCell>
    <TableCell className="font-mono-num text-right font-medium">
      <TxnAmount type={t.type} amount={t.amount} />
    </TableCell>
    <TableCell className="font-mono-num text-right text-slate-900">
      {formatINR(t.balance_after)}
    </TableCell>
  </TableRow>
);

function Rows({ txns, loading, onEditDate, onShare }) {
  if (loading) return <MessageRow>Loading transactions…</MessageRow>;
  if (txns.length === 0) return <MessageRow>No transactions yet.</MessageRow>;
  return txns.map((t) => <TxnRow key={t.id} t={t} onEditDate={onEditDate} onShare={onShare} />);
}

export const TransactionsTable = ({ txns, loading = false, onEditDate, onShare, balanceLabel = "Balance", testid }) => (
  <Table data-testid={testid}>
    <TableHeader>
      <TableRow className="bg-slate-50 hover:bg-slate-50">
        <TableHead className={HEAD}>Date</TableHead>
        <TableHead className={HEAD}>Type</TableHead>
        <TableHead className={HEAD}>Note</TableHead>
        <TableHead className={`${HEAD} text-right`}>Amount</TableHead>
        <TableHead className={`${HEAD} text-right`}>{balanceLabel}</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <Rows txns={txns} loading={loading} onEditDate={onEditDate} onShare={onShare} />
    </TableBody>
  </Table>
);
